// @ts-nocheck
/**
 * "Your action item list is getting long" prompt.
 *
 * Shows on load when the workspace is carrying more than BACKLOG_THRESHOLD open items, with a
 * CTA into the swipe cleaner. Dismissing snoozes it for a week per workspace per browser, so a
 * backlog nobody intends to clear today stops being a daily interruption.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';

const BACKLOG_THRESHOLD = 100;
const SNOOZE_DAYS = 7;

function snoozeKey(workspaceId: string, userId: string) {
  return `upstand.actionBacklog.snoozedUntil.${workspaceId}.${userId}`;
}

function isSnoozed(workspaceId: string, userId: string): boolean {
  try {
    const raw = localStorage.getItem(snoozeKey(workspaceId, userId));
    if (!raw) return false;
    const until = Number(raw);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false; // private mode / blocked storage: show it rather than swallow it
  }
}

export function snoozeActionBacklogBanner(workspaceId: string, userId: string) {
  try {
    localStorage.setItem(
      snoozeKey(workspaceId, userId),
      String(Date.now() + SNOOZE_DAYS * 86_400_000),
    );
  } catch {
    /* storage unavailable - the banner just comes back next load */
  }
}

export function ActionItemBacklogBanner({
  workspaceId,
  userId,
  workspaceName,
  onStartCleanup,
}: {
  workspaceId?: string | null;
  userId?: string | null;
  workspaceName?: string | null;
  onStartCleanup: () => void;
}) {
  const [count, setCount] = useState<number | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!workspaceId || !userId) return;
    if (isSnoozed(workspaceId, userId)) return;
    let cancelled = false;
    void (async () => {
      const { count: openCount, error } = await supabase
        .from('action_items')
        .select('id, meetings!inner(workspace_id)', { count: 'exact', head: true })
        .eq('meetings.workspace_id', workspaceId)
        .in('status', ['open', 'in_progress']);
      if (cancelled || error) return;
      setCount(openCount ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, userId]);

  if (hidden || count === null || count <= BACKLOG_THRESHOLD) return null;

  const dismiss = () => {
    setHidden(true);
    if (workspaceId && userId) snoozeActionBacklogBanner(workspaceId, userId);
  };

  return (
    <div className="mx-4 mt-3 flex shrink-0 flex-wrap items-center gap-3 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 to-primary-container/10 px-4 py-3">
      <Sparkles className="h-5 w-5 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">
          {count} open action items {workspaceName ? `in ${workspaceName}` : 'on your list'}
        </p>
        <p className="text-xs text-muted-foreground">
          Clear the dead ones in a couple of minutes. One item at a time, three keys, nothing
          deleted for good.
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          setHidden(true);
          if (workspaceId && userId) snoozeActionBacklogBanner(workspaceId, userId);
          onStartCleanup();
        }}
        className="shrink-0"
      >
        Clean up
      </Button>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
